import { useConsentStore } from '../store/consent-store';
import { optInAnalytics, optOutAnalytics } from './analytics';

/**
 * Consent → SDK bridge (ADR-0065).
 *
 * Subscribes to `useConsentStore` once at boot and flips each
 * provider's gate when the user's decision changes. Affiliates have
 * no SDK to flip — `useAffiliatesEnabled` reads consent at render
 * time and re-subscribes itself via Zustand's selector mechanism.
 * Error reporting has no gate of its own either: it shares the PostHog
 * client with analytics, so `optInAnalytics` / `optOutAnalytics` already
 * govern whether exceptions reach the SDK (ADR-0065).
 *
 * Extracted from `app/_layout.tsx` so the side-effect can be exercised
 * by unit tests without dragging the Expo Router + Tamagui import
 * tree into a Node test environment.
 */

let unsubscribe: (() => void) | null = null;

/**
 * Install the bridge. Idempotent — repeat calls return the existing
 * unsubscribe handle without registering a second listener (defensive
 * against React Native's Fast Refresh re-importing the module).
 */
export function installConsentBridge(): () => void {
  if (unsubscribe) return unsubscribe;
  unsubscribe = useConsentStore.subscribe((state, prev) => {
    if (state.consent === prev.consent) return;
    const c = state.consent;
    if (c?.analytics) optInAnalytics();
    else optOutAnalytics();
  });
  return unsubscribe;
}

/** Test-only — drop the subscription so each `it()` starts from
 *  a clean slate. */
export function _uninstallConsentBridgeForTests(): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
}
